
import React, { useState } from 'react';
import { Modal, Upload, Button, message, Table } from 'antd';
import { UploadOutlined, SaveOutlined } from '@ant-design/icons';
import { ParAssetCatalogResidualValue } from 'services/assetconfig';
import ButtonComponent from 'components/Button';
interface CSVModalGridProps {
    onSaveOrEdit: (record: any) => any;
    form: any;
    id: number;
    fieldName: string;
    refetch: () => void;
}

const CSVModalGrid: React.FC<CSVModalGridProps> = ({
    onSaveOrEdit,
    form,
    id,
    fieldName,
    refetch,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fileName, setFileName] = useState('');
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    const handleClearData = () => {
        setData([]);
        setColumns([]);
        setFileName('');
        setSelectedRows([]);
    };

    const processCSV = (str: string, delim = ',') => {
        const headers = str.slice(0, str.indexOf('\n')).split(delim);
        const rows = str.slice(str.indexOf('\n') + 1).split('\n');

        const defaultRow: ParAssetCatalogResidualValue = {
            catalogid: id,
            monthsmax: 0,
            monthsmin: 0,
            mileagemax: 0,
            mileagemin: 0,
            exceedformula: 0,
            prcode: '',
            rvevaluationmode: '',
            rvfixedamount: 0,
            rvid: -1,
            rvpercentage: 0,
            trg: 0,
            rvtype: '',
            monthdecrease: 0,
            rvstartmethod: '',
            countrvid: 0,
        };

        const tableColumns: any = headers.map((header) => ({
            title: header.trim(),
            dataIndex: header.trim(),
            key: header.trim(),
            sorter: (a: any, b: any) => {
                const aValue = a[header.trim()];
                const bValue = b[header.trim()];
                return !isNaN(aValue) && !isNaN(bValue)
                    ? Number(aValue) - Number(bValue)
                    : String(aValue).localeCompare(String(bValue));
            },
            ellipsis: true,
            width: 150,
        }));

        const newData = rows
            .filter((row) => row.trim())
            .map((row, index) => {
                const values = row.split(delim);
                const record:any = { ...defaultRow };

                // Map each field from the CSV to the corresponding property in the object
                headers.forEach((header, i) => {
                    const field = header.trim();
                    if (Object.prototype.hasOwnProperty.call(record, field)) {
                        const value = values[i]?.trim();
                        record[field] = isNaN(Number(value)) ? value : Number(value) || record[field];
                    }
                });
                // Ensure rvid is always -1
                record.rvid = -1;
                // Assign additional properties
                return {
                    ...record,
                    key: index,
                };
            });

        setColumns(tableColumns);
        setData(newData);
    };

    const uploadProps = {
        accept: '.csv',
        showUploadList: false,
        beforeUpload: (file: any) => {
            setLoading(true);
            setFileName(file.name);

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;
                    processCSV(text);
                    message.success(`${file.name} uploaded successfully`);
                    setIsModalOpen(true);
                } catch (error) {
                    message.error('Error processing CSV file');
                    console.error('Error processing CSV:', error);
                } finally {
                    setLoading(false);
                }
            };
            reader.onerror = () => {
                message.error('Error reading file');
                setLoading(false);
            };
            reader.readAsText(file);
            return false;
        },
    };

    const handleSaveBulk = async () => {
        if (selectedRows.length === 0) {
            message.warning('Please select rows to save');
            return;
        }

        setLoading(true);
        try {
            // Save all rows concurrently
            await Promise.all(selectedRows.map((record) => onSaveOrEdit(record)));

            message.success(`${selectedRows.length} rows saved successfully`);
            refetch();
            setIsModalOpen(false);
        } catch (error) {
            message.error('Error saving rows');
            console.error('Bulk save error:', error);
        } finally {
            setLoading(false);
        }
    };

    const rowSelection = {
        onChange: (selectedRowKeys: any, selectedRows: any) => {
            setSelectedRows(selectedRows);
        },
    };

    return (
        <>
            <div className='ut-uploadBtn'>
                <Upload {...uploadProps} style={{ width: '100%' }}>
                    <ButtonComponent
                        icon={<UploadOutlined />}
                        loading={loading}
                        type="primary"
                        comcode="Import"
                    />

                </Upload>

          </div>
           
            <Modal
                title={`CSV Data - ${fileName}`}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                width="80%"
                footer={[
                    <Button
                        key="save"
                        icon={<SaveOutlined />}
                        type="primary"
                        onClick={handleSaveBulk}
                        loading={loading}
                        disabled={selectedRows.length === 0}
                    >
                        Save Selected ({selectedRows.length})
                    </Button>,
                   
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                        Close
                    </Button>,
                ]}
            >
                {data.length > 0 ? (
                    <Table
                        rowSelection={{
                            type: 'checkbox',
                            ...rowSelection,
                        }}
                        columns={columns}
                        dataSource={data}
                        scroll={{ x: 'max-content', y: '60vh' }}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `Total ${total} items`,
                        }}
                        size="small"
                        bordered
                    />
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No data available
                    </div>
                )}
            </Modal>
        </>
    );
};

export default CSVModalGrid;
